import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const SAVE_CONFIRMATION_DURATION_MS = 1800;

export type SaveSource = "next" | "prev" | "queue" | "setOfficial";

export type SaveConfirmation = {
  source: SaveSource;
  accessId: string;
};

type AssessmentGradingFormContextType = {
  isAiAssessmentLoading: boolean;
  setIsAiAssessmentLoading: (loading: boolean) => void;
  saveConfirmation: SaveConfirmation | null;
  showSaveConfirmation: (confirmation: SaveConfirmation) => void;
};

const AssessmentGradingFormContext = createContext<AssessmentGradingFormContextType | undefined>(
  undefined,
);

// eslint-disable-next-line react-refresh/only-export-components
export const useAssessmentGradingFormContext = () => {
  const ctx = useContext(AssessmentGradingFormContext);
  if (!ctx)
    throw new Error(
      "useAssessmentGradingFormContext must be used within AssessmentGradingFormProvider",
    );
  return ctx;
};

export const AssessmentGradingFormProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isAiAssessmentLoading, setIsAiAssessmentLoading] = useState(false);
  const [saveConfirmation, setSaveConfirmation] = useState<SaveConfirmation | null>(null);
  const saveConfirmationTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const showSaveConfirmation = useCallback((confirmation: SaveConfirmation) => {
    clearTimeout(saveConfirmationTimeout.current);
    setSaveConfirmation(confirmation);
    saveConfirmationTimeout.current = setTimeout(
      () => setSaveConfirmation(null),
      SAVE_CONFIRMATION_DURATION_MS,
    );
  }, []);

  useEffect(() => () => clearTimeout(saveConfirmationTimeout.current), []);

  const value = useMemo(
    () => ({
      isAiAssessmentLoading,
      setIsAiAssessmentLoading,
      saveConfirmation,
      showSaveConfirmation,
    }),
    [isAiAssessmentLoading, saveConfirmation, showSaveConfirmation],
  );

  return (
    <AssessmentGradingFormContext.Provider value={value}>
      {children}
    </AssessmentGradingFormContext.Provider>
  );
};
